<script setup>
import { useStore } from 'vuex'
import { computed } from 'vue'
import Badge from './Badges/Badge.vue'
import identity from 'lodash/identity'
import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import pickBy from 'lodash/pickBy'
import { useLocalization } from '../composables/useLocalization'

const { __ } = useLocalization()

/**
 * Vuex store
 */
const store = useStore()

/**
 * User menu items formatted for rendering
 */
const formattedItems = computed(() => {
  return store.getters.userMenu.map(i => {
    let method = i.method || 'GET'
    let props = { href: i.path }

    if (i.external && method === 'GET') {
      return {
        component: 'a',
        props: {
          ...props,
          target: i.target || null,
        },
        name: i.name,
        external: i.external,
        on: {},
      }
    }

    return {
      component: method === 'GET' ? 'a' : 'FormButton',
      props: pickBy(
        omitBy(
          {
            ...props,
            method: method !== 'GET' ? method : null,
            data: i.data || null,
            headers: i.headers || null,
          },
          isNull
        ),
        identity
      ),
      external: i.external,
      name: i.name,
      on: {},
      badge: i.badge,
    }
  })
})

/**
 * Current user display name
 */
const userName = computed(() => {
  return (
    store.getters.currentUser?.name ||
    store.getters.currentUser?.email ||
    __('Nova User')
  )
})

/**
 * Current user email
 */
const userEmail = computed(() => {
  const user = store.getters.currentUser

  if (!user?.email || user.email === userName.value) {
    return null
  }

  return user.email
})

/**
 * Custom logout path from Nova config
 */
const customLogoutPath = computed(() => Nova.config('customLogoutPath'))

/**
 * Whether authentication logout is supported
 */
const supportsAuthentication = computed(() => {
  return (
    Nova.config('withAuthentication') === true ||
    customLogoutPath.value !== false
  )
})

/**
 * Whether the mobile user footer should render
 */
const hasUserMenu = computed(() => {
  return (
    store.getters.currentUser &&
    (formattedItems.value.length > 0 ||
      supportsAuthentication.value ||
      store.getters.currentUser?.impersonating)
  )
})

/**
 * Whether secondary menu links should render below the identity row
 */
const hasSecondaryActions = computed(() => {
  return (
    formattedItems.value.length > 0 ||
    store.getters.currentUser?.impersonating
  )
})

/**
 * Emits the logout confirmation event
 */
const attempt = () => {
  Nova.$emit('confirm-logout')
}

/**
 * Stops impersonating the current user after confirmation
 */
const handleStopImpersonating = () => {
  if (confirm(__('Are you sure you want to stop impersonating?'))) {
    store.dispatch('stopImpersonating')
  }
}

/**
 * Closes the mobile drawer after navigating
 */
const closeDrawer = () => {
  if (store.getters.mainMenuShown) {
    store.commit('toggleMainMenu')
  }
}
</script>

<template>
  <div v-if="hasUserMenu" class="panel-mobile-drawer-footer">
    <div class="panel-mobile-drawer-footer__header">
      <div class="panel-mobile-drawer-footer__avatar" aria-hidden="true">
        <Icon
          type="finger-print"
          :solid="true"
          v-if="store.getters.currentUser?.impersonating"
          class="size-4 text-amber-500"
        />
        <img
          v-else-if="store.getters.currentUser?.avatar"
          :alt="__(':name\'s Avatar', { name: userName })"
          :src="store.getters.currentUser?.avatar"
          class="size-full rounded-full object-cover"
        />
        <span
          v-else
          class="flex size-full items-center justify-center rounded-full bg-primary-500/15 text-xs font-bold text-primary-500"
        >
          {{ userName.charAt(0).toUpperCase() }}
        </span>
      </div>

      <div class="panel-mobile-drawer-footer__identity">
        <p class="panel-mobile-drawer-footer__name">
          {{ userName }}
        </p>
        <p v-if="userEmail" class="panel-mobile-drawer-footer__email">
          {{ userEmail }}
        </p>
      </div>

      <button
        v-if="supportsAuthentication"
        type="button"
        class="panel-mobile-drawer-footer__logout"
        :aria-label="__('Logout')"
        @click="attempt"
      >
        <svg
          class="size-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
      </button>
    </div>

    <nav
      v-if="hasSecondaryActions"
      class="panel-mobile-drawer-footer__links"
      :aria-label="__('User menu')"
    >
      <component
        :is="item.component"
        v-for="item in formattedItems"
        :key="item.props?.href || item.name"
        v-bind="item.props"
        v-on="item.on"
        class="panel-mobile-drawer-footer__link"
        @click="closeDrawer"
      >
        <span v-if="item.badge" class="shrink-0">
          <Badge :extra-classes="item.badge.typeClass">
            {{ item.badge.value }}
          </Badge>
        </span>

        <span class="truncate">{{ item.name }}</span>
      </component>

      <button
        type="button"
        v-if="store.getters.currentUser?.impersonating"
        class="panel-mobile-drawer-footer__link"
        @click="handleStopImpersonating"
      >
        <span class="truncate">{{ __('Stop Impersonating') }}</span>
      </button>
    </nav>
  </div>
</template>
